<template>
  <v-dialog v-model="dialogActive" fullscreen>
    <template v-slot:activator="{ on }">
      <v-badge v-if="section.actions && section.actions.length > 0" :content="section.actions.length" overlap>
        <v-btn v-on="on">Edit To-Do Items</v-btn>
      </v-badge>
      <v-btn v-else v-on="on">Add To-Do Items</v-btn>
      <v-dialog v-model="actionHelpDialog" width="600">
        <template v-slot:activator="{ on: dialogOn }">
          <v-icon v-on="dialogOn" color="info" style="margin-right:.5em;">fas fa-question-circle</v-icon>
        </template>
        <v-card>
          <v-card-title>ToDo Items</v-card-title>
          <v-card-text>These are to-do-list items (i.e. links) that will be available to the student once they complete this section, which can either point to an action built within the orientation system or a separate webpage (like Canvas, Banner, etc). Some items will automatically clear when the student completes them (such as the internal action for pre-registration), but others will remain until the student dismisses them, allowing them to complete or dismiss the to-do-list item in their own timeframe.</v-card-text>
          <v-card-actions>
            <v-btn text @click="actionHelpDialog = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
    <v-card>
      <v-toolbar>
        <v-toolbar-title>To-Do Items</v-toolbar-title>
      </v-toolbar>
      <v-card-text>
        <p style="margin-top:16px">These are to-do-list items that will be added when the student has completed this section.</p>
        <v-row>
          <v-col>
            <v-list elevation="4">
              <v-subheader>List of Items</v-subheader>
              <v-list-item v-for="({ title, link }, index) in section.actions" :key="'action-' + index" @click="select(index)">
                <v-list-item-content>
                  <v-list-item-title>{{ title }}</v-list-item-title>
                  <v-list-item-subtitle>{{ link }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-btn color="success" @click="select(-1)">Add Item</v-btn>
              </v-list-item>
            </v-list>
          </v-col>
          <v-col v-if="selectedIndex != null">
            <v-text-field v-model="title" label="Text" outlined></v-text-field>
            <v-combobox v-model="link" :items="actionOptions" label="Link" outlined>
              <template v-slot:append-outer>
                <v-dialog v-model="linkHelpModal" width="400">
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" color="info">fas fa-question-circle</v-icon>
                  </template>
                  <v-card>
                    <v-card-title>Link</v-card-title>
                    <v-card-text>This link can be set to an external website (in which case clicking on the link will open in a new tab) or it can be an internal action built within the system. These built-in actions are part of the autocomplete, but any web address can be entered. All exernal web addresses must start with http:// or https://.</v-card-text>
                    <v-card-actions>
                      <v-btn text @click="linkHelpModal = false">Close</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </template>
            </v-combobox>
            <date-picker v-model="date" label="Available Date" clear-button>
              <template v-slot:append-outer>
                <v-dialog v-model="dateHelpModal" width="400">
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" color="info">fas fa-question-circle</v-icon>
                  </template>
                  <v-card>
                    <v-card-title>Available Date</v-card-title>
                    <v-card-text>If this date is set, then it marks when this action will become available. If the date is not set, then the action will be available upon completion of the current section. If the date is set, then if a student completes the current section before this date then the action will not be made available until this date has been reached. If the date is already past this date then the action will be made available immediately.</v-card-text>
                    <v-card-actions>
                      <v-btn text @click="dateHelpModal = false">Close</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </template>
            </date-picker>
            <v-select v-model="condition" :items="conditionOptions" label="Show Conditionally" clearable outlined></v-select>
            <v-checkbox v-model="isPlacementTest" label="Is Placement Test" style="margin-top:0">
              <template v-slot:append>
                <v-dialog v-model="testHelpModal" width="400">
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" color="info">fas fa-question-circle</v-icon>
                  </template>
                  <v-card>
                    <v-card-title>Is Placement Test</v-card-title>
                    <v-card-text>Placement tests will block access to the pre-registration action, and will also be presented in a dialog box. Checking this box will require the student to complete this to-do item before accessing the pre-registration form, as well as opening a dialog with the text specified below.</v-card-text>
                    <v-card-actions>
                      <v-btn text @click="testHelpModal = false">Close</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </template>
            </v-checkbox>
            <html-editor v-if="isPlacementTest" v-model="dialogText" style="margin-bottom:1em"></html-editor>
            <v-toolbar elevation="0">
              <v-btn :disabled="selectedIndex in section.actions && title === section.actions[selectedIndex].title && link === section.actions[selectedIndex].link && date === section.actions[selectedIndex].date" @click="save">Save Changes</v-btn>
              <v-spacer></v-spacer>
              <v-btn v-if="selectedIndex >= 0" color="error" @click="$emit('remove', selectedIndex)">Remove Item</v-btn>
            </v-toolbar>
          </v-col>
          <v-col v-else>
            <p>Select an item from the list on the left or click the button to add a new item.</p>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-btn @click="dialogActive = false" text>Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { computed, ref, watch } from '@vue/composition-api'
import paths from '@/router/paths'
export default {
  components: {
    DatePicker: () => import('../forms/inputs/DatePicker'),
    HtmlEditor: () => import('../forms/HTMLEditor')
  },
  props: {
    section: {
      type: Object,
      default: () => { return {} }
    }
  },
  setup (props, { root, emit }) {
    const dialogActive = ref(false)
    const actionHelpDialog = ref(false)

    const title = ref('')
    const link = ref('')
    const date = ref('')
    const condition = ref('')
    const isPlacementTest = ref(false)
    const dialogText = ref('')
    const selectedIndex = ref(null)

    function select (index) {
      if (index >= 0) {
        title.value = props.section.actions[index].title
        link.value = props.section.actions[index].link
        date.value = 'openDate' in props.section.actions[index] && props.section.actions[index].openDate != null ? props.section.actions[index].openDate.substr(0, 10) : ''
        if ('condition' in props.section.actions[index] && typeof (props.section.actions[index].condition) === 'object') {
          let temp = props.section.actions[index].condition
          if ('itemIndex' in temp && temp.itemIndex >= 0) {
            condition.value = temp.id + '.items.' + temp.itemIndex
          } else {
            condition.value = temp.id
          }
        } else {
          condition.value = ''
        }
        isPlacementTest.value = ('isPlacementTest' in props.section.actions[index] ? props.section.actions[index].isPlacementTest : false)
        dialogText.value = ('dialogText' in props.section.actions[index] ? props.section.actions[index].dialogText : '')
        selectedIndex.value = index
      } else {
        title.value = ''
        link.value = ''
        date.value = ''
        condition.value = ''
        isPlacementTest.value = false
        dialogText.value = ''
        selectedIndex.value = -1
      }
    }

    function save () {
      let obj = { title: title.value, link: link.value, openDate: date.value }
      if (condition.value !== '') {
        const regex = /.items.[0-9]+$/i
        if (regex.test(condition.value)) {
          let temp = condition.value.split('.items.')
          obj.condition = { id: temp[0], itemIndex: parseInt(temp[1]) }
        } else {
          obj.condition = { id: condition.value, itemIndex: 0 }
        }
      }
      if (isPlacementTest.value) {
        obj.isPlacementTest = isPlacementTest.value
        obj.dialogText = dialogText.value
      }
      if (selectedIndex.value >= 0) {
        obj.index = selectedIndex.value
        emit('update', obj)
      } else {
        emit('add', obj)
      }
    }

    const actionOptions = computed(() => {
      let arr = []
      for (let i = 0; i < paths.length; i++) {
        if (paths[i].path.substr(0, 8) === '/action/') arr.push(paths[i].path)
      }
      return arr
    })

    const conditionOptions = ref([])
    watch(() => props.section, async () => {
      let arr = []
      const service = root.$feathers.service('orientation/block')
      // Look at each slide, and each column within each slide, and each item within each column, and determine if the slide might contain interactive content (quiz, agreement, popups, timeline)
      for (const { primary, secondary } of props.section.slides) {
        for (const { id, type, name } of primary) {
          // If so, retrieve that content block so we can determine if it does have one or more items that could be used as a condition
          if (type === 'QuizCard') {
            // let data = await service.get(_id)
          } else if (type === 'Agreement') {
            arr.push({ value: id, text: name })
          } else if (type === 'Popups' || type === 'Timeline') {
            let data = await service.get(id)
            if (data.activatable) {
              for (let i = 0; i < data.items.length; i++) {
                if ('actions' in data.items[i] && 'activate' in data.items[i].actions && data.items[i].actions.activate !== '') {
                  arr.push({ value: id + '.items.' + i, text: 'Activated: ' + data.items[i].title })
                }
              }
            }
          }
        }
        for (const { id, type, name } of secondary) {
          // If so, retrieve that content block so we can determine if it does have one or more items that could be used as a condition
          if (type === 'QuizCard') {
            // let data = await service.get(_id)
          } else if (type === 'Agreement') {
            arr.push({ value: id, text: name })
          } else if (type === 'Popups' || type === 'Timeline') {
            let data = await service.get(id)
            if (data.activatable) {
              for (let i = 0; i < data.items.length; i++) {
                if ('actions' in data.items[i] && 'activate' in data.items[i].actions && data.items[i].actions.activate !== '') {
                  arr.push({ value: id + '.items.' + i, text: 'Activated: ' + data.items[i].title })
                }
              }
            }
          }
        }
      }
      conditionOptions.value = arr
    })

    const dateHelpModal = ref(false)
    const linkHelpModal = ref(false)
    const testHelpModal = ref(false)

    return {
      dialogActive,
      actionHelpDialog,
      title,
      link,
      date,
      condition,
      isPlacementTest,
      dialogText,
      selectedIndex,
      select,
      save,
      actionOptions,
      conditionOptions,
      dateHelpModal,
      linkHelpModal,
      testHelpModal
    }
  }
}
</script>
